import _get from 'lodash.get';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { captureException } from '../../../services/sentryService';
import { Nullable } from '../../../types';
import CoreProviders, { CoreProvidersProps } from '../../CoreProviders';
import { useErrorContext } from '../../ErrorContextProvider';
import { AdyenConfig } from '../Adyen.types';
import { AdyenEnvironment } from '../Forms/AdyenCreditCardForm.static';

import AdyenApplePayButton from './AdyenApplePayButton';
import { ApplePayErrorType } from './AdyenApplePayButton.static';

interface InnerAdyenApplePayButtonProps extends AdyenConfig {
  countryCode: string;
  currency: string;
  amount: number;
  paymentMethodsResponse: unknown;
  onSubmit: (applePayToken: string) => void;
  onError: (errorType: Nullable<string>) => void;
}

function getApplePayErrorType(errorType: Nullable<string>): ApplePayErrorType {
  return errorType === 'CANCEL' ? 'payment' : 'initialization';
}

const InnerAdyenApplePayButton: React.FC<InnerAdyenApplePayButtonProps> = (props) => {
  const { t } = useTranslation();
  const { setError } = useErrorContext();

  const onFatalError = useCallback(
    (error: unknown) => {
      const errorType: Nullable<string> = _get(error, 'name', null);
      setError({
        message: t(`checkout.payment.applepay.error.${getApplePayErrorType(errorType)}`),
      });

      captureException({ error });
      props.onError(errorType);
    },
    [props.onError],
  );

  return <AdyenApplePayButton {...props} onFatalError={onFatalError} />;
};

export interface AdyenApplePayButtonWrapperProps extends CoreProvidersProps {
  adyenEnvironment: AdyenEnvironment;
  countryCode: string;
  currency: string;
  amount: number;
  paymentMethodsResponse: unknown;
  onSubmit: (applePayToken: string) => void;
  onError: (errorType: Nullable<string>) => void;
}

/*
 * Wrapper over the true component, because we first need to call the CoreProviders.
 */
const AdyenApplePayButtonWrapper: React.FC<AdyenApplePayButtonWrapperProps> = ({
  env,
  shopBundle,
  locale,
  domain,
  errorContainerId,
  adyenEnvironment,
  countryCode,
  currency,
  amount,
  paymentMethodsResponse,
  onSubmit,
  onError,
}) => {
  // to be replaced with the clientKey from the ConfigProvider, once tests are successful
  const clientKey = 'test_CN2KJT2FBNHAJK5FX5R6275GAI4JLFP3';

  return (
    <CoreProviders
      env={env}
      shopBundle={shopBundle}
      locale={locale}
      domain={domain}
      errorContainerId={errorContainerId}>
      <InnerAdyenApplePayButton
        adyenEnvironment={adyenEnvironment}
        clientKey={clientKey}
        countryCode={countryCode}
        currency={currency}
        amount={amount}
        paymentMethodsResponse={paymentMethodsResponse}
        onSubmit={onSubmit}
        onError={onError}
      />
    </CoreProviders>
  );
};

export default AdyenApplePayButtonWrapper;
